class HvacCardVariants {
  constructor(container) {
    this.container = container;
    const variantsEl = container.querySelector('[data-hvac-variants]');
    this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    this.priceEl = container.querySelector('[data-hvac-price]');
    this.variantInput = container.querySelector('[data-hvac-variant-id]');
    this.addBtn = container.querySelector('[data-hvac-add-btn]');
    this.productUnavailable = container.dataset.productUnavailable === 'true';
    const wrapper = container.closest('.card-wrapper');
    this.skuEl = wrapper ? wrapper.querySelector('[data-hvac-sku]') : container.querySelector('[data-hvac-sku]');
    this.titleEl = wrapper ? wrapper.querySelector('[data-hvac-title]') : null;
    this.imgEl = wrapper ? wrapper.querySelector('.card__media img') : null;
    this.currentOptions = null;
    // null means not yet set; onVariantChange() or data-hvac-base-price will initialize it
    this.baseVariantPrice = null;

    // Single-variant (simple) cards have no [data-hvac-variants] so we read the
    // base price directly from the data attribute set in the Liquid template.
    const basePriceAttr = container.dataset.hvacBasePrice;
    if (basePriceAttr !== undefined && basePriceAttr !== '') {
      this.baseVariantPrice = parseInt(basePriceAttr, 10);
    }

    // The visible blue price spans (regular and/or sale)
    this.bluePriceEls = wrapper
      ? wrapper.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale')
      : [];
    this.compareAtEls = wrapper
      ? wrapper.querySelectorAll('.price__sale s.price-item--regular, .price__regular s.price-item--regular')
      : [];
    this.saveEl = wrapper ? wrapper.querySelector('[data-hvac-save]') : null;
    this.monthlyEl = wrapper ? wrapper.querySelector('[data-hvac-monthly]') : null;
    this.currentCompareAtPrice = 0;

    this.container.addEventListener('change', (e) => {
      if (e.target.classList.contains('hvac-pill-input') || e.target.classList.contains('hvac-acc-select')) {
        this.onVariantChange();
      }
    });

    const accGrid = container.querySelector('.hvac-acc-grid');
    if (accGrid) {
      accGrid.addEventListener('change', (e) => {
        if (e.target.classList.contains('hvac-acc-select')) {
          this.updateTotalPrice();
        }
      });
    }

    this.initTitle();
    this.onVariantChange();
  }

  // Returns all accessory selects inside the card container
  getAccSelects() {
    return [...this.container.querySelectorAll('.hvac-acc-select')];
  }

  // Parses "(+$45.00)" → 4500 cents
  parseAccPrice(value) {
    const match = value.match(/\(\+\$([0-9,]+\.?\d*)\)/);
    return match ? Math.round(parseFloat(match[1].replace(/,/g, '')) * 100) : 0;
  }

  // Sums base variant price + all selected accessory prices and updates the price display
  updateTotalPrice() {
    if (this.baseVariantPrice === null) return;
    let total = this.baseVariantPrice;
    this.getAccSelects().forEach((sel) => {
      total += this.parseAccPrice(sel.value);
    });
    const formatted = this.formatMoney(total);
    if (this.priceEl) this.priceEl.textContent = formatted;
    this.bluePriceEls.forEach((el) => { el.textContent = formatted; });
    this.updateMonthly(total);
  }

  formatMoneyNoZeros(cents) {
    const val = cents / 100;
    const opts = Number.isInteger(val)
      ? { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : { style: 'currency', currency: 'USD' };
    return new Intl.NumberFormat('en-US', opts).format(val);
  }

  // Shop Pay Installments monthly payment at 15% APR, amortized.
  // Same math the Liquid template renders — see snippets/card-product.liquid.
  installmentMonthly(totalCents, months) {
    const r = 0.15 / 12;
    const growth = Math.pow(1 + r, months);
    return Math.round(totalCents * r * growth / (growth - 1));
  }

  updateMonthly(totalCents) {
    if (!this.monthlyEl || !totalCents || totalCents <= 0) return;
    if (totalCents < 15000) {
      this.monthlyEl.hidden = true;
      return;
    }
    this.monthlyEl.hidden = false;
    const amountEls = this.monthlyEl.querySelectorAll('[data-hvac-monthly-amount]');
    amountEls.forEach((el) => {
      const months = parseInt(el.dataset.hvacMonths, 10);
      if (!months) return;
      el.textContent = `${this.formatMoneyNoZeros(this.installmentMonthly(totalCents, months))}/mo`;
    });
  }

  updateSave(compareCents, priceCents) {
    if (!this.saveEl) return;
    const diff = (compareCents || 0) - (priceCents || 0);
    if (diff > 0) {
      const pct = Math.round((diff / compareCents) * 100);
      this.saveEl.textContent = `Save ${this.formatMoneyNoZeros(diff)} (${pct}%)`;
      this.saveEl.removeAttribute('hidden');
    } else {
      this.saveEl.textContent = '';
      this.saveEl.setAttribute('hidden', '');
    }
  }

  updateCompareAt(compareCents, priceCents) {
    if (!this.compareAtEls || !this.compareAtEls.length) return;
    if (compareCents && compareCents > (priceCents || 0)) {
      const formatted = this.formatMoney(compareCents);
      this.compareAtEls.forEach((el) => { el.textContent = formatted; });
    }
  }

  getSelectedOptions() {
    const options = [];
    this.container.querySelectorAll('.hvac-option-group').forEach((group) => {
      const checked = group.querySelector('.hvac-pill-input:checked');
      const select = group.querySelector('.hvac-acc-select');
      options.push(checked ? checked.value : (select ? select.value : null));
    });
    return options;
  }

  findVariant(selectedOptions) {
    return this.variants.find((v) =>
      selectedOptions.every((opt, i) => v.options[i] === opt)
    );
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }

  updateOptionAvailability(selectedOptions) {
    const groups = this.container.querySelectorAll('.hvac-option-group');
    groups.forEach((group, optionIndex) => {
      group.querySelectorAll('.hvac-pill-input').forEach((input) => {
        const testOptions = [...selectedOptions];
        testOptions[optionIndex] = input.value;
        const match = this.variants.find((v) =>
          testOptions.every((opt, i) => opt === null || v.options[i] === opt)
        );
        const available = match && match.available;
        const label = this.container.querySelector(`label[for="${input.id}"]`);
        if (label) label.classList.toggle('hvac-pill--unavailable', !available);
      });
    });
  }

  // Replaces a full option value in the title, tolerating singular/plural drift
  // between the pill label ("2 Tons") and the title text ("2 Ton").
  //
  // For numeric option values (tonnage, SEER, etc.) we require the value to stand
  // alone — bordered by whitespace or a string edge — so swapping "2" for "2.5"
  // never touches the "2" embedded inside "SEER2", "R32", "40K", etc.
  //
  // When the title uses a marketing SEER number the store option doesn't literally
  // contain (e.g. title "15.3 SEER3" but options 14.3/15.2), fall back to a regex
  // swap of whatever digits sit immediately before the "SEER" keyword.
  replaceOption(title, oldVal, newVal, optionName) {
    if (/^\d+\.?\d*$/.test(oldVal)) {
      const escaped = oldVal.replace(/\./g, '\\.');
      const re = new RegExp('(^|\\s)' + escaped + '(?=\\s|$)');
      if (re.test(title)) return title.replace(re, '$1' + newVal);
    } else if (title.includes(oldVal)) {
      return title.split(oldVal).join(newVal);
    }
    const oldSing = oldVal.replace(/s\b/i, '');
    const newSing = newVal.replace(/s\b/i, '');
    if (oldSing !== oldVal && title.includes(oldSing)) {
      return title.split(oldSing).join(newSing);
    }
    if (optionName && /seer/i.test(optionName) && /^\d+\.?\d*$/.test(newVal)) {
      const swapped = title.replace(/\d+\.?\d*(?=\s*SEER)/i, newVal);
      if (swapped !== title) return swapped;
      // Merchant left the efficiency out of the title entirely — inject it right
      // after the tonnage phrase so the shopper can see the SEER2 they've picked.
      if (!/SEER/i.test(title)) {
        const tonMatch = title.match(/\d+\.?\d*\s+Tons?\b/i);
        if (tonMatch) {
          return title.replace(tonMatch[0], tonMatch[0] + ' ' + newVal + ' SEER2');
        }
      }
    }
    return title;
  }

  // Reads the human label above each pill group so replaceOption can tell
  // "SEER2 Efficiency" from "System Tonnage" and only apply SEER-specific fallbacks.
  getOptionNames() {
    const names = [];
    this.container.querySelectorAll('.hvac-option-group').forEach((group) => {
      const label = group.querySelector('.hvac-option-label');
      names.push(label ? label.textContent.replace(/^Select\s+/i, '').replace(/:$/, '').trim() : '');
    });
    return names;
  }

  // Finds any variant SKU currently sitting in `text` (longest-first so "RC318"
  // isn't matched inside "RC318E2S11"). Returns the matched substring verbatim
  // so the caller can preserve its original casing on replace.
  findAnySkuIn(text) {
    if (!text) return null;
    const skus = [];
    this.variants.forEach((v) => { if (v.sku && skus.indexOf(v.sku) < 0) skus.push(v.sku); });
    skus.sort((a, b) => b.length - a.length);
    for (const sku of skus) {
      const re = new RegExp(sku.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      const m = text.match(re);
      if (m) return m[0];
    }
    return null;
  }

  // Swaps whatever variant SKU is currently in the title for the newly-selected
  // one. Combined-listing titles like
  // "Guardian 1.5 Ton 13.4 SEER2 Air Conditioner Condenser - R-454B - RC318E2S11"
  // bake the model into the title; without this the card would show "RC318E2S11"
  // for every variant.
  replaceSkuInTitle(title, newSku) {
    if (!newSku) return title;
    const found = this.findAnySkuIn(title);
    if (!found || found.toLowerCase() === newSku.toLowerCase()) return title;
    return title.split(found).join(newSku);
  }

  // Called once on init: aligns the product title to the initially-checked pills.
  // Sorts pill values longest-first so "1.5 Ton" is matched before "5 Ton"
  // and we never accidentally replace a substring.
  initTitle() {
    const selectedOptions = this.getSelectedOptions();
    const optionNames = this.getOptionNames();
    if (this.titleEl) {
      let title = this.titleEl.textContent.trim();
      this.container.querySelectorAll('.hvac-option-group').forEach((group) => {
        const optIdx = parseInt(group.dataset.optionIndex);
        if (isNaN(optIdx)) return;
        const newVal = selectedOptions[optIdx];
        if (!newVal) return;
        const inputs = Array.from(group.querySelectorAll('.hvac-pill-input'))
          .sort((a, b) => b.value.length - a.value.length);
        for (const input of inputs) {
          const oldVal = input.value;
          if (oldVal === newVal) continue;
          if (newVal.includes(oldVal)) continue; // e.g. skip "5 Ton" when newVal is "1.5 Ton"
          const replaced = this.replaceOption(title, oldVal, newVal, optionNames[optIdx]);
          if (replaced !== title) { title = replaced; break; }
        }
      });
      const initialVariant = this.findVariant(selectedOptions);
      if (initialVariant && initialVariant.sku) {
        title = this.replaceSkuInTitle(title, initialVariant.sku);
      }
      this.titleEl.textContent = title;
    }
    this.currentOptions = [...selectedOptions];
  }

  // On each change, replaces only the previously-known option value with the new one.
  // Using currentOptions (exact previous values) avoids any substring collision.
  updateTitle(variant) {
    if (!this.titleEl || !variant || !this.currentOptions) return;
    let title = this.titleEl.textContent.trim();
    const optionNames = this.getOptionNames();
    variant.options.forEach((newVal, i) => {
      const oldVal = this.currentOptions[i];
      if (oldVal && newVal && oldVal !== newVal) {
        title = this.replaceOption(title, oldVal, newVal, optionNames[i]);
      }
    });
    title = this.replaceSkuInTitle(title, variant.sku);
    this.titleEl.textContent = title;
  }

  onVariantChange() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariant(selectedOptions);

    if (variant) {
      this.baseVariantPrice = variant.price; // overrides the data-attr value when variants exist
      this.currentCompareAtPrice = variant.compare_at_price || 0;
      if (this.priceEl) this.priceEl.textContent = this.formatMoney(variant.price);
      if (this.variantInput) this.variantInput.value = variant.id;
      if (this.skuEl) {
        if (variant.sku && variant.id) {
          const itemNo = (Number(variant.id) % 9000000) + 1000000;
          this.skuEl.textContent = `Item: ${itemNo} | Model: ${variant.sku}`;
        } else {
          this.skuEl.textContent = '';
        }
      }
      this.updateTitle(variant);
      this.currentOptions = [...selectedOptions];
      this.updateSave(this.currentCompareAtPrice, variant.price);
      this.updateCompareAt(this.currentCompareAtPrice, variant.price);
      this.updateTotalPrice();
      // Keep the product link in sync so clicking through loads the right variant
      if (this.titleEl) {
        const baseHref = (this.titleEl.getAttribute('href') || '').split('?')[0];
        if (baseHref) this.titleEl.setAttribute('href', baseHref + '?variant=' + variant.id);
      }

      if (this.addBtn && !this.productUnavailable) {
        this.addBtn.disabled = !variant.available;
        const span = this.addBtn.querySelector('span:not(.loading__spinner)') || this.addBtn;
        span.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      }
      if (variant.featured_image && this.imgEl) {
        this.updateCardImage(variant.featured_image);
      }
    } else {
      if (this.addBtn) {
        this.addBtn.disabled = true;
        const span = this.addBtn.querySelector('span:not(.loading__spinner)') || this.addBtn;
        span.textContent = 'Unavailable';
      }
    }

    this.updateOptionAvailability(selectedOptions);
  }

  // Inserts _Nx size hint before the file extension in a Shopify CDN URL
  shopifyImageSrc(src, width) {
    if (!src) return src;
    return src.replace(/(\.[a-zA-Z]+)(\?.*)?$/, `_${width}x$1$2`);
  }

  updateCardImage(featuredImage) {
    if (!featuredImage || !this.imgEl) return;
    const src = featuredImage.src;
    const maxW = featuredImage.width || 1066;
    const widths = [165, 360, 533, 720, 940, 1066].filter((w) => w <= maxW);
    if (!widths.includes(maxW)) widths.push(maxW);
    this.imgEl.srcset = widths.map((w) => `${this.shopifyImageSrc(src, w)} ${w}w`).join(', ');
    this.imgEl.src = this.shopifyImageSrc(src, 533);
    if (featuredImage.alt != null) this.imgEl.alt = featuredImage.alt;
  }
}

function initHvacCards() {
  document.querySelectorAll('[data-hvac-card]').forEach((card) => {
    if (!card._hvacInit) {
      card._hvacInit = true;
      new HvacCardVariants(card);
    }
  });
}

/* Raise the whole card while its panel is open. Each card is its own stacking
   context (Dawn's `.card--card.gradient { transform: perspective(0) }`), so a
   z-index on the panel alone leaves it underneath the next card in the grid. */
function syncHvacAccOpenCards() {
  document.querySelectorAll('.hvac-acc-card-open').forEach((el) => {
    el.classList.remove('hvac-acc-card-open');
  });
  document.querySelectorAll('.hvac-acc-accordion[open]').forEach((acc) => {
    const wrapper = acc.closest('.card-wrapper');
    const card = acc.closest('.card');
    if (wrapper) wrapper.classList.add('hvac-acc-card-open');
    if (card) card.classList.add('hvac-acc-card-open');
  });
}

/* Close other accordions when one opens */
document.addEventListener('toggle', (e) => {
  if (!e.target.classList || !e.target.classList.contains('hvac-acc-accordion')) return;
  if (e.target.classList.contains('hvac-acc-accordion--disabled') && e.target.open) {
    e.target.open = false;
    return;
  }
  if (e.target.open) {
    document.querySelectorAll('.hvac-acc-accordion').forEach((acc) => {
      if (acc !== e.target) acc.open = false;
    });
  }
  syncHvacAccOpenCards();
}, true);

/* Close open accordion when clicking outside it */
document.addEventListener('click', (e) => {
  if (e.target.closest('.hvac-acc-accordion')) return;
  document.querySelectorAll('.hvac-acc-accordion[open]').forEach((acc) => {
    acc.open = false;
  });
}, false);


if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHvacCards);
} else {
  initHvacCards();
}

/* === Accessories overlay backdrop === */
(function () {
  var BACKDROP_ID = 'hvac-acc-backdrop';

  function ensureBackdrop() {
    if (document.getElementById(BACKDROP_ID)) return;
    var el = document.createElement('div');
    el.id = BACKDROP_ID;
    document.body.appendChild(el);
  }

  function removeBackdrop() {
    var el = document.getElementById(BACKDROP_ID);
    if (el) el.remove();
  }

  /* Derive from the page, not from this one accordion: opening card B also
     closes card A, and A's toggle fires last — keying off e.target.open alone
     tore the backdrop down while B was still open. */
  document.addEventListener('toggle', function (e) {
    if (!e.target.classList || !e.target.classList.contains('hvac-acc-accordion')) return;
    if (document.querySelector('.hvac-acc-accordion[open]')) ensureBackdrop();
    else removeBackdrop();
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.hvac-acc-accordion[open]').forEach(function (acc) {
      acc.open = false;
    });
  });
})();
